import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { IState, IStateOptions } from 'angular-ui-router';
import { IStateService } from 'angular-ui-router';
import { assign, compact, each, extend, includes, map, sortBy, valuesIn } from 'lodash';
import { Subject, Unsubscribable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SpinnerModal } from '../../../app/shared/modals/status-spinner/status-spinner.service';
import { Toggles } from '../../shared/constants/toggles.constant';
import { ObjectCache } from '../../shared/services/object-cache/object-cache.service';
import { PortalConfig } from '../../shared/services/portal-config';
import { ToggleService } from '../../shared/services/toggle/toggle.service';
import { TValidSchoolTypes } from '../../shared/typings/interfaces/school.interface';
import { IUser } from '../../shared/typings/interfaces/user.interface';
import { getSchool, getToggleEntities } from '../../store';
import { ImSchool } from './../../shared/services/im-models/im-school';
import { ImUser } from './../../shared/services/im-models/im-user';
import { UserAgentService } from './../../shared/services/user-agent.service.ts/user-agent.service';

interface INavGroup {
  [propName: string]: INavItem;
}
interface INavItem {
  humanName: string;
  isActiveState: string;
  goState: string;
  isRestricted: boolean;
  isExternalUrl?: boolean;
  order?: number;
}

/* istanbul ignore next */
@Component({
  selector: 'nvps-side-nav',
  template: require('./side-nav.component.html'),
  styles: [require('./side-nav.component.scss')],
})
export class SideNavNgComponent implements OnInit, OnDestroy {
  @Input() currentUser: any;

  public selectedView: 'School' | 'Network';
  private isClusterUser: boolean;
  private networkToggleOn: boolean;
  private publicConfig: PortalConfig['publicConfig'];

  constructor(
    @Inject('$state') private $state: IStateService,
    @Inject('$window') private $window: any,
    @Inject('$location') private $location: any,
    private objectCache: ObjectCache,
    @Inject('SpinnerModal') private SpinnerModal: SpinnerModal,
    @Inject('User') private User,
    private portalConfig: PortalConfig,
    private imUser: ImUser,
    private imSchool: ImSchool,
    private Store: Store<any>,
    private userAgentService: UserAgentService,
    private toggleService: ToggleService,
  ) {
    this.publicConfig = this.portalConfig.publicConfig;
  }

  public NV_ENV;
  public nickname: string;
  public schoolType: TValidSchoolTypes;
  private isTransferSchool: boolean;
  public sidenav;
  public destroy$: Subject<boolean> = new Subject<boolean>();
  private isHs: boolean;
  private isHybridSchool: boolean;
  private isSchoolWideEditor: boolean;
  private isSummerSchool: boolean;
  private isCaseloadUser: boolean;
  private currentTermNumber: number;

  public toggleSub: Unsubscribable;
  public schoolSub: Unsubscribable;

  ngOnInit() {
    this.selectedView = 'School';
    this.isClusterUser = this.imUser.isClusterUser(this.currentUser);
    this.isSchoolWideEditor = this.imUser.isSchoolWideEditor(this.currentUser);
    this.isCaseloadUser = this.imUser.isCaseloadUser(this.currentUser);
    this.NV_ENV = this.publicConfig.DISPLAY_NV_ENV;

    // check for Network Dashboard Toggle
    this.toggleSub = this.Store.select(getToggleEntities)
      .pipe(takeUntil(this.destroy$))
      .subscribe(toggles => {
        const { TOGGLE_NETWORK_DASHBOARD } = toggles;
        this.networkToggleOn = TOGGLE_NETWORK_DASHBOARD;
      });

    this.schoolSub = this.Store.select(getSchool)
      .pipe(takeUntil(this.destroy$))
      .subscribe(school => {
        this.nickname = school.nickName;
        this.schoolType = school.schoolType;
        this.isHs = this.imSchool.isHighSchool(school);
        this.isHybridSchool = this.imSchool.isHybridSchool(school);
        this.isTransferSchool = this.imSchool.isTransferSchool(school);
        this.isSummerSchool = this.imSchool.isSummerSchool(school);
        this.currentTermNumber = this.imSchool.getCurrentTermNumber(school);
        this.sidenav = {
          v3: {},
          v2: {},
        };
        /**
         * V2 side nav links to migrate / confirm removal of - JYR
         */

        //   AFFINITY_SITE: {
        //     humanName: 'Affinity Site',
        //     icon: 'web',
        //     isActiveState: null,
        //     goState: null,
        //     isRestricted: false,
        //     externalUrl: 'http://affinity.newvisions.org/'
        //   },
        //   CURRICULUM_SITE: {
        //     humanName: 'Curriculum Site',
        //     icon: 'web',
        //     isActiveState: null,
        //     goState: null,
        //     isRestricted: false,
        //     externalUrl: 'http://www.newvisions.org/pages/curriculum-and-instruction'
        //   },
        //   CLOUDLAB_SITE: {
        //     humanName: 'CloudLab Site',
        //     icon: 'web',
        //     isActiveState: null,
        //     goState: null,
        //     isRestricted: false,
        //     externalUrl: 'http://cloudlab.newvisions.org/',
        //   },
        const sdcDefaultView = this.imUser.getDefaultSdcView(this.currentUser);
        const settingsRoute = this.getSettingsFirstDefaultSubRoute();
        const isCaseloadUser = this.imUser.isCaseloadUser(this.currentUser);
        const options = {
          listOptions: {
            allSchoolTypes: {
              TILES: {
                humanName: 'Dashboard',
                isActiveState: 'main.school.lists.tiles',
                goState: 'main.school.lists.tiles',
                // prevent caseload users from seeing dashboard side-nav
                isRestricted: isCaseloadUser ? true : null,
                isExternalUrl: false,
                toggleAccess: true,
                queryParams: { Dimension1: null, Dimension2: null, Groupings: null },
                order: 0,
              } as INavItem,
              MY_STUDENTS_LIST: {
                humanName: 'Students',
                isActiveState: 'main.school.lists.students',
                goState: 'main.school.lists.students',
                isRestricted: null,
                isExternalUrl: false,
                toggleAccess: true,
                queryParams: {
                  focus: null,
                  filter: null,
                  grouping: null,
                  sortKey: null,
                  sortDirection: null,
                  search: null,
                },
                order: 1,
              } as INavItem,
              ATTENDANCE_LIST: {
                humanName: 'Attendance',
                isActiveState: 'main.school.lists.attendance',
                goState: 'main.school.lists.attendance',
                isRestricted: null,
                isExternalUrl: false,
                toggleAccess: true,
                queryParams: {
                  focus: null,
                  filter: null,
                  grouping: null,
                  sortKey: null,
                  sortDirection: null,
                  search: null,
                },
                order: 2,
              } as INavItem,
              ACADEMIC_LIST: {
                humanName: 'Academic',
                isActiveState: 'main.school.lists.academic',
                goState: 'main.school.lists.academic',
                isRestricted: null,
                isExternalUrl: false,
                toggleAccess: true,
                queryParams: {
                  focus: null,
                  filter: null,
                  grouping: null,
                  sortKey: null,
                  sortDirection: null,
                  search: null,
                },
                order: 3,
              } as INavItem,
              SUMMER_SCHOOL_LIST: {
                humanName: 'Summer School',
                isActiveState: 'main.school.lists.summer-school',
                goState: 'main.school.lists.summer-school',
                isRestricted: null,
                isExternalUrl: false,
                toggleAccess: true,
                queryParams: {
                  focus: null,
                  filter: null,
                  grouping: null,
                  sortKey: null,
                  sortDirection: null,
                  search: null,
                },
                order: 6,
              } as INavItem,
            } as INavGroup,

            allHsTypes: {
              POSTSECONDARY_LIST: {
                humanName: 'Postsecondary',
                isActiveState: 'main.school.lists.postsecondary',
                goState: 'main.school.lists.postsecondary',
                isRestricted: null,
                isExternalUrl: false,
                toggleAccess: true,
                queryParams: {
                  focus: null,
                  filter: null,
                  grouping: null,
                  sortKey: null,
                  sortDirection: null,
                  search: null,
                },
                order: 4,
              } as INavItem,

              // Per requirements on https://newvisions.atlassian.net/browse/PFD-7450 , keep this code until further notice.

              // REGENTS_EXEMPTIONS_LIST: {
              //   humanName: 'Regents',
              //   isActiveState: 'main.school.lists.regents-exemptions',
              //   goState: 'main.school.lists.regents-exemptions',
              //   isRestricted: null,
              //   isExternalUrl: false,
              //   toggleAccess: true,
              //   queryParams: {
              //     focus: null,
              //     filter: null,
              //     grouping: null,
              //     sortKey: null,
              //     sortDirection: null,
              //     search: null,
              //   },
              //   order: 5,
              // } as INavItem,
            } as INavGroup,
          },

          nonListOptions: {
            general: {
              MY_SUPPORTS: {
                humanName: 'My supports',
                isActiveState: 'main.school.my-supports',
                goState: 'main.school.my-supports',
                isRestricted: null,
                isExternalUrl: false,
              } as INavItem,
              OTHER_TOOLS: {
                humanName: 'Other tools',
                isActiveState: 'main.school.other-tools.default',
                goState: 'main.school.other-tools.default',
                isRestricted: null,
                isExternalUrl: false,
              } as INavItem,
            } as INavGroup,

            smallNavMenu: {
              SETTINGS: {
                humanName: 'Settings',
                isActiveState: settingsRoute,
                goState: settingsRoute,
                isRestricted: null,
                isExternalUrl: false,
              } as INavItem,
              DATA_SOURCES: {
                humanName: 'Data Uploads',
                isActiveState: 'main.school.data-uploads.gradebook',
                goState: 'main.school.data-uploads.gradebook',
                isRestricted: null,
                isExternalUrl: false,
              } as INavItem,
              FORMATIVE_ASSESSMENTS: {
                humanName: 'Formative assessments',
                isActiveState: 'main.school.data-entry.assessments',
                goState: 'main.school.data-entry.assessments',
                isRestricted: null,
                isExternalUrl: false,
              } as INavItem,
              REPORT_ISSUES: {
                humanName: 'Report Issues',
                isActiveState: null,
                goState: null,
                isRestricted: null,
                isExternalUrl: true,
              } as INavItem,
              PRIVACY_AND_TERMS: {
                humanName: 'Privacy & Terms',
                isActiveState: 'privacy-and-terms',
                goState: 'privacy-and-terms',
                isRestricted: null,
                isExternalUrl: false,
              } as INavItem,
            } as INavGroup,
            // NOTE: goState is set to sdc `grid` by default since the state
            // determines whether the link is show or not shown
            // for a user. All users can currently see the `grid`. The `wizard` is limited to certain users. (CM)
            sdc: {
              DEFAULT_SDC_GRID: {
                humanName: 'Data grid',
                sdcStateRoot: 'main.school.new-sdc.default-sdc.grid',
                isActiveState: 'main.school.new-sdc.default-sdc.grid',
                goState: 'main.school.new-sdc.default-sdc.grid',
                isRestricted: null,
              } as INavItem,
              GRAD_PLANS: {
                humanName: 'Grad plans',
                isActiveState: 'main.school.new-sdc.grad-planning',
                goState: `main.school.new-sdc.grad-planning.${sdcDefaultView}`,
                isRestricted: null,
                hasStateParams: true,
                isExternalUrl: false,
              } as INavItem,
              CREDIT_GAPS: {
                humanName: 'Credit gaps',
                isActiveState: 'main.school.new-sdc.credit-gaps',
                goState: `main.school.new-sdc.credit-gaps.${sdcDefaultView}`,
                isRestricted: null,
                hasStateParams: true,
                isExternalUrl: false,
              } as INavItem,
              REGENTS_PLANNING: {
                humanName: 'Regents planning',
                isActiveState: 'main.school.new-sdc.regents-planning',
                goState: `main.school.new-sdc.regents-planning.${sdcDefaultView}`,
                isRestricted: null,
                hasStateParams: true,
                isExternalUrl: false,
              } as INavItem,
              REGENTS_PREP: {
                humanName: 'Regents prep',
                isActiveState: 'main.school.new-sdc.regents-prep',
                goState: `main.school.new-sdc.regents-prep.${sdcDefaultView}`,
                isRestricted: null,
                hasStateParams: true,
                isExternalUrl: false,
              } as INavItem,
            } as INavGroup,
          },
        };

        const {
          listOptions: { allSchoolTypes, allHsTypes },
          nonListOptions: { general, sdc, smallNavMenu },
        } = options;

        const filteredSmallNav = Object.assign({}, smallNavMenu);

        // filters small nav options that are school type specific and toggle specific (JE)
        // TODO: remove this once toggle is on for all schools
        const nyctToggleOn = this.toggleService.getToggleState(Toggles.TOGGLE_NYCT_MVP);
        if (!nyctToggleOn || (this.isHs && !this.isHybridSchool)) delete filteredSmallNav.FORMATIVE_ASSESSMENTS;
        if (!this.isHs) delete filteredSmallNav.DATA_SOURCES;
        if (this.currentTermNumber !== 7 || !this.isSummerSchool)
          delete options.listOptions.allSchoolTypes.SUMMER_SCHOOL_LIST;
        if (this.isHs) {
          // supply args in order of how the individuals sidenav option groups should appear
          const orderedMultiSchoolTypeOptions = sortBy({ ...allSchoolTypes, ...allHsTypes }, 'order');
          const v3Options = this.getOptions(orderedMultiSchoolTypeOptions, sdc, general);
          const v2Options = this.getOptions(sdc, general);
          const smallNavOptions = this.getOptions(filteredSmallNav);
          this.sidenav.v3 = this.generateMenu(v3Options);
          this.sidenav.v2 = this.generateMenu(v2Options);
          this.sidenav.smallNav = this.generateMenu(smallNavOptions);
        } else {
          const v3Options = this.getOptions(allSchoolTypes, general);
          const v2Options = this.getOptions(general);
          const smallNavOptions = this.getOptions(filteredSmallNav);
          this.sidenav.v3 = this.generateMenu(v3Options);
          this.sidenav.v2 = this.generateMenu(v2Options);
          this.sidenav.smallNav = this.generateMenu(smallNavOptions);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private getOptions(...links): Object {
    return valuesIn(assign({}, ...links));
  }

  // Check user role and return menu items to be rendered for current user. Not applicable to list views
  private generateMenu(options): Object {
    options = this.isRestricted(options);
    return compact(
      map(options, value => {
        return value;
      }),
    );
  }

  // For some reason, in passing resolved data into ng2 component via states defined with ui-router,
  // if the data contains instance methods, they are arriving completed dropped.
  // Not ideal to have to go instantiate a new user, but neccesary for now. - JYR
  private getCurrentUser(currentUser): IUser {
    const user = new this.User({
      _id: currentUser._id,
      nvRole: currentUser.nvRole,
      delegatedRole: currentUser.delegatedRole,
    });
    return user;
  }

  isActive(stateName: string): boolean {
    return includes(this.$state.current.name, stateName);
  }

  // Restrict this schools links based on current user
  isRestricted(opts): boolean {
    return each(opts, (v: any, key) => {
      // if not otherwise set, get restriction
      if (v.isRestricted === null && v.goState) {
        v.isRestricted = !this.imUser.canAccessState(this.currentUser, v.goState);
      }

      // links that should be restricted for current user are removed after user check
      if (v.isRestricted) {
        delete opts[key];
      }
    });
  }

  /**
   * DEPRECATE?
   * The this.isRestricted method checks current user's role and compares it to state permissions
   * constant for each state so I think this.isClusterUser can go away,
   * but lets leave this for a little while just in case.
   *
   * Note: This was only being called by SWITCH_SCHOOL.isResricted - JYR
   *
   */
  // isClusterUser(): boolean {
  //   if (this.imUser.isEmpty(this.currentUser)) {
  //     return false;
  //   }
  //   return this.imUser.isClusterUser(this.currentUser);
  // }
  /*
  manually calls state transition here instead of relying on ui-sref. necessary.
  goThere() is necessary due to a ui-router issue that is documented here:
  https://github.com/angular-ui/ui-router/issues/395#issuecomment-56884667
  */
  goState(state: IState, params: IStateOptions): void {
    if (state === this.$state.current.name) {
      return;
    } else if (state === 'privacy-and-terms') {
      const url = this.$state.href('privacy-and-terms');
      this.$window.open(url, '_blank');
    } else if (state === 'main.school.new-sdc.default-sdc.grid') {
      this.SpinnerModal.activateSpinner('Loading...');
      this.$state.go(state);
    } else {
      this.SpinnerModal.activateSpinner('Loading...');
      this.$state.go(state, extend(this.$state.params, params));
    }
  }

  goExternal(name): void {
    switch (name) {
      case 'Report Issues':
        this.$window.FreshWidget.show();
        break;
      default:
        return null;
    }
  }

  /**
   * DEPRECATE? - JYR
   * Changing ag-grid filters does not update $state.params or $stateParams
   * $location service does get updated with query params
   *
   * NOTE: Unclear whether we need this.
   *
   */
  filterParam() {
    return { filter: this.$location.$$search.filter || this.objectCache.cacheObject({}) };
  }

  onViewToggle(viewName: 'School' | 'Network'): void {
    this.selectedView = viewName;
    if (viewName === 'School') this.goToSchool();
    else this.goToNetwork();
  }

  goToSchool(): void {
    const { schoolId } = this.$state.params;
    if (schoolId) this.$state.go('main.school.home', { schoolId });
    else this.switchSchool();
  }

  goToNetwork(): void {
    const schoolView = 'main.network.dashboard';
    const { schoolId } = this.$state.params;
    let params = schoolId ? { schoolId } : null;
    this.$state.go(schoolView, params);
  }

  switchSchool(): void {
    const lastView = this.$state.current.name;
    const params = { lastView, stateParams: this.$state.params };
    this.$state.go('main.cluster', params);
  }

  // Returns the first default sub route when on settings route,
  // depends on the school type and user role type
  private getSettingsFirstDefaultSubRoute(): string {
    let route;
    if (this.isHs) {
      // high schoool is transfer school, small hs, large hs, hybrid school
      if (this.isSchoolWideEditor || this.isCaseloadUser) {
        route = 'main.school.settings.supports.all-supports';
      } else {
        // --> at this point, it is school admin, delegated school admin
        if (this.isTransferSchool) {
          route = 'main.school.settings.metrics.distance-to-grad';
        } else {
          route = 'main.school.settings.metrics.credits';
        }
      }
    } else {
      // for middle or elementary school
      route = 'main.school.settings.supports.all-supports';
    }
    return route;
  }
}
